import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, ChevronUp, ChevronDown } from 'lucide-react';
import './SearchablePayload.css';

// A read-only <pre> for decoded payloads with an inline search box: shows the occurrence count,
// highlights every match, and lets you jump between them (Enter / ↑ / ↓). Reused by every message-log
// modal so decoded reservation/profile XML is easy to search.
export default function SearchablePayload({ text, className = '' }) {
    const [q, setQ] = useState('');
    const [active, setActive] = useState(0);
    const markRefs = useRef([]);

    // Split the text into plain + matched segments (case-insensitive), numbering the matches.
    const parts = useMemo(() => {
        const src = text == null ? '' : String(text);
        if (!q) return [{ text: src, match: false }];
        const out = [];
        const hay = src.toLowerCase();
        const needle = q.toLowerCase();
        let from = 0, i, mi = 0;
        while ((i = hay.indexOf(needle, from)) !== -1) {
            if (i > from) out.push({ text: src.slice(from, i), match: false });
            out.push({ text: src.slice(i, i + q.length), match: true, mi: mi++ });
            from = i + q.length;
        }
        if (from < src.length) out.push({ text: src.slice(from), match: false });
        return out;
    }, [text, q]);

    const total = useMemo(() => parts.reduce((n, p) => n + (p.match ? 1 : 0), 0), [parts]);

    useEffect(() => { setActive(0); }, [q]);
    useEffect(() => {
        const el = markRefs.current[active];
        if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, [active, total]);

    const go = (delta) => { if (total > 0) setActive((a) => ((a + delta) % total + total) % total); };

    return (
        <div className="sp-wrap">
            <div className="sp-search">
                <Search size={13} className="sp-search-icon" />
                <input
                    className="sp-input"
                    placeholder="Search payload…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); go(e.shiftKey ? -1 : 1); } }}
                />
                {q && <span className="sp-count">{total === 0 ? 'No matches' : `${active + 1} of ${total}`}</span>}
                <button type="button" className="sp-nav" title="Previous (Shift+Enter)" onClick={() => go(-1)} disabled={total === 0}><ChevronUp size={15} /></button>
                <button type="button" className="sp-nav" title="Next (Enter)" onClick={() => go(1)} disabled={total === 0}><ChevronDown size={15} /></button>
            </div>
            <pre className={className}>
                {parts.map((p, i) =>
                    p.match
                        ? <mark key={i} ref={(el) => { markRefs.current[p.mi] = el; }} className={`sp-mark${p.mi === active ? ' sp-mark-active' : ''}`}>{p.text}</mark>
                        : <React.Fragment key={i}>{p.text}</React.Fragment>
                )}
            </pre>
        </div>
    );
}
